import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { flowsheetUrl } from './api';
import { clientApi } from '@/config/axios';
import type { ClinicalSubject } from './types';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth/AuthContext';
import { usePermissions } from '@/features/permissions';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
interface State {
  active_subject_ids: string[] | null;
  closed: boolean;
  fetus_count: number;
  history: { version: number; action: string; reason: string; recorded_at: string; author_id: string }[];
  portal_visibility: 'PRIVATE';
  version: number;
}
export function EpisodeAdministration({ subjects, episodeId }: { episodeId: string; subjects: ClinicalSubject[] }) {
  const { organization } = useAuth();
  const { permissions } = usePermissions();
  const org = organization?.organization_id;
  const cache = useQueryClient();
  const url = `${flowsheetUrl(org!)}/episodes/${episodeId}/administration`;
  const query = useQuery({
    enabled: !!org,
    queryKey: ['episode-administration', org, episodeId],
    queryFn: async () => (await clientApi.get<State>(url)).data,
  });
  const [retained, setRetained] = useState<string[]>([]);
  const [add, setAdd] = useState(0);
  const [kind, setKind] = useState('RECONCILE');
  const [reason, setReason] = useState('');
  const [reviewed, setReviewed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [retry, setRetry] = useState<Record<string, unknown> | null>(null);
  useEffect(() => {
    if (query.data && !retry) {
      const active = subjects.filter(
        (s) =>
          s.subject_type === 'FETUS' &&
          (query.data!.active_subject_ids === null || query.data!.active_subject_ids.includes(s.id)),
      );
      setRetained(active.map((s) => s.id));
      setAdd(Math.max(0, query.data.fetus_count - active.length));
      setReviewed(false);
    }
  }, [query.data, subjects, retry]);
  const save = async () => {
    if (!query.data) return;
    const input = retry || {
      kind,
      reviewed: true,
      reason: reason.trim(),
      request_id: crypto.randomUUID(),
      expected_version: query.data.version,
      add_fetuses: kind === 'RECONCILE' ? add : 0,
      expected_fetus_count: query.data.fetus_count,
      retained_fetus_ids: kind === 'RECONCILE' ? retained : [],
    };
    setRetry(input);
    setBusy(true);
    try {
      await clientApi.post(url, input);
      setRetry(null);
      setReason('');
      setReviewed(false);
      await cache.invalidateQueries({ queryKey: ['clinical-episodes', org] });
      await cache.invalidateQueries({ queryKey: ['episode-administration', org, episodeId] });
      setMessage('Episode administration recorded. Historical observations remain unchanged.');
    } catch {
      setMessage(
        'Change was not confirmed. Retry the same request safely, or discard this review and reload current state.',
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card>
      <CardHeader>
        <CardTitle>Episode administration</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p>
          Clinical flowsheets are private to authorized practice users. They are not published to a patient portal.
          Closing stops new observations while preserving corrections, review and history.
        </p>
        {query.isError && <p role="alert">Episode status could not be loaded.</p>}
        {query.data && (
          <>
            <p>
              {query.data.closed ? 'Closed' : 'Open'} · {query.data.fetus_count} documented fetuses · revision{' '}
              {query.data.version}
            </p>
            {permissions.includes('manage_clinical_episode') && (
              <>
                <fieldset className="space-y-3" disabled={busy || !!retry}>
                  <label>
                    Action{' '}
                    <select
                      value={kind}
                      className="min-h-11 border"
                      onChange={(e) => {
                        setKind(e.target.value);
                        setReviewed(false);
                      }}>
                      <option value="RECONCILE">Reconcile fetal subjects</option>
                      <option value="CLOSE">Close episode</option>
                      <option value="REOPEN">Reopen episode</option>
                    </select>
                  </label>
                  {kind === 'RECONCILE' && (
                    <>
                      <p>
                        Choose which existing fetal identities remain active. New identities get new IDs. Retired
                        subjects and their observations are retained; no observation is reassigned.
                      </p>
                      {subjects
                        .filter((s) => s.subject_type === 'FETUS')
                        .map((s) => (
                          <label key={s.id} className="block">
                            <input
                              type="checkbox"
                              checked={retained.includes(s.id)}
                              onChange={(e) => {
                                setRetained((ids) =>
                                  e.target.checked ? [...ids, s.id] : ids.filter((id) => id !== s.id),
                                );
                                setReviewed(false);
                              }}
                            />
                            {s.label} · {s.id} {s.active === false ? '(inactive)' : ''}
                          </label>
                        ))}
                      <label>
                        New fetal identities{' '}
                        <input
                          min={0}
                          max={10}
                          value={add}
                          type="number"
                          className="min-h-11 border w-20"
                          onChange={(e) => {
                            setAdd(Number(e.target.value));
                            setReviewed(false);
                          }}
                        />
                      </label>
                      <p>
                        {retained.length + add} selected / {query.data.fetus_count} documented. Update the pregnancy’s
                        documented fetus count first if needed.
                      </p>
                    </>
                  )}
                  <label className="block">
                    Reason
                    <textarea
                      rows={3}
                      value={reason}
                      maxLength={2000}
                      className="w-full border"
                      onChange={(e) => {
                        setReason(e.target.value);
                        setReviewed(false);
                      }}
                    />
                  </label>
                  <label>
                    <input type="checkbox" checked={reviewed} onChange={(e) => setReviewed(e.target.checked)} />I
                    reviewed the identities and clinical implications of this change.
                  </label>
                </fieldset>
                <Button
                  onClick={() => void save()}
                  disabled={
                    busy ||
                    (!retry &&
                      (!reviewed ||
                        !reason.trim() ||
                        (kind === 'RECONCILE' && retained.length + add !== query.data.fetus_count)))
                  }>
                  {retry ? 'Retry exact reviewed change' : 'Apply reviewed change'}
                </Button>
                {retry && (
                  <Button
                    disabled={busy}
                    variant="outline"
                    onClick={() => {
                      setRetry(null);
                      setReviewed(false);
                      void query.refetch();
                    }}>
                    Discard review and reload
                  </Button>
                )}
              </>
            )}
            <details>
              <summary>Administration history</summary>
              {query.data.history.map((h) => (
                <p key={h.version}>
                  {h.recorded_at} · {h.action} · {h.reason} · author {h.author_id}
                </p>
              ))}
            </details>
          </>
        )}
        {message && <p role="status">{message}</p>}
      </CardContent>
    </Card>
  );
}
